import {
  ActionError,
  AttentionBlock,
  Button,
  ChoiceRow,
  Field,
  FieldGrid,
  FormBlock,
  Input,
  FormFooter,
  PageHeader,
  PageTitle,
  Radio,
  RadioGroup,
  ReadingColumn,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router'
import { Page } from '@/app/shell'
import {
  AddressBlock,
  ContactBlock,
  IdentificationBlock,
  TextField,
} from '@/features/clinic-profile/blocks'
import {
  docTypeOf,
  formatDocNumber,
  isValidCNPJ,
  normalizeDocNumber,
} from '@/features/clinic-profile/doc-number'
import {
  ADDRESS_KEYS,
  CONTACT_KEYS,
  DOC_NUMBER_KEYS,
  EMPTY_CLINIC_FORM,
  IDENTIFICATION_KEYS,
  clinicFieldKey,
  companyInput,
  emailError,
  formatPostalCode,
  personNameError,
  placeFieldErrors,
  validateClinicForm,
  type ClinicFormState,
} from '@/features/clinic-profile/form'
import { focusFirstInvalid, useFormState } from '@/features/clinic-profile/use-form-state'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import {
  useCreateClinic,
  useLookupCnpj,
  type AccessMode,
  type CnpjRegistration,
  type CreatedClinic,
} from './api'
import { AudiencesBlock } from './audiences-block'
import { REGISTRATION_STATUS_LABELS, clinicName } from './labels'
import { NetworkBlock } from './network-block'

/**
 * Nova clínica (docs/areas/administracao.md, "Nova clínica"; RN-ADM-01, 03, 04, 50, 54 e 57).
 * Arquétipo: cadastro, só de root. A clínica nasce com a primeira gestora, tudo ou nada; o resto
 * ela mesma completa na implantação. A rede é opcional e escolhida entre as existentes (P-ADM-83).
 */

interface NewClinicFormState extends ClinicFormState {
  managerName: string
  managerEmail: string
  accessMode: AccessMode
  /** O id da rede escolhida; vazio é "Sem rede". */
  networkId: string
}

const EMPTY: NewClinicFormState = {
  ...EMPTY_CLINIC_FORM,
  managerName: '',
  managerEmail: '',
  accessMode: 'invite',
  networkId: '',
}

const KEYS = [
  ...DOC_NUMBER_KEYS,
  ...IDENTIFICATION_KEYS,
  ...ADDRESS_KEYS,
  ...CONTACT_KEYS,
  'managerName',
  'managerEmail',
] as const satisfies readonly (keyof NewClinicFormState)[]

function validate(values: NewClinicFormState) {
  return {
    ...validateClinicForm(values),
    managerName: personNameError(values.managerName),
    managerEmail: emailError(values.managerEmail),
  }
}

const MANAGER_FIELD_KEYS: Record<string, keyof NewClinicFormState> = {
  firstManager: 'managerName',
  'firstManager.name': 'managerName',
  'firstManager.email': 'managerEmail',
  'firstManager.accessMode': 'accessMode',
  networkId: 'networkId',
}

const fieldKey = (path: string, values: NewClinicFormState) =>
  MANAGER_FIELD_KEYS[path] ?? clinicFieldKey(path, values)

/** O que a base pública devolveu, nos campos do formulário; o que ela não tem fica como está. */
function fromRegistration(registration: CnpjRegistration): Partial<NewClinicFormState> {
  const { address } = registration
  return {
    legalName: registration.legalName,
    ...(registration.tradeName ? { tradeName: registration.tradeName } : {}),
    ...(address.postalCode ? { postalCode: formatPostalCode(address.postalCode) } : {}),
    ...(address.street ? { street: address.street } : {}),
    ...(address.number ? { number: address.number } : {}),
    ...(address.complement ? { complement: address.complement } : {}),
    ...(address.district ? { district: address.district } : {}),
    ...(address.city ? { city: address.city } : {}),
    ...(address.state ? { state: address.state } : {}),
  }
}

export function NewClinicPage() {
  const navigate = useNavigate()
  const create = useCreateClinic()
  const lookup = useLookupCnpj()
  const form = useFormState<NewClinicFormState>(() => EMPTY, validate)
  const formRef = useRef<HTMLFormElement>(null)
  // Com senha temporária, a tela fica para mostrar a senha uma única vez (RN-ADM-50).
  const [created, setCreated] = useState<CreatedClinic | null>(null)

  function changeDocNumber(text: string) {
    const docNumber = formatDocNumber(text)
    const doc = normalizeDocNumber(docNumber)
    const changed = doc !== normalizeDocNumber(form.values.docNumber)
    form.update({ docNumber })
    if (!changed) return
    // Só o CNPJ completo e válido é consultado; CPF não tem base pública (RN-ADM-04).
    if (doc.length !== 14 || !isValidCNPJ(doc)) {
      lookup.reset()
      return
    }
    lookup.mutate(doc, { onSuccess: (registration) => form.update(fromRegistration(registration)) })
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!form.check(KEYS)) {
      focusFirstInvalid(formRef.current)
      return
    }
    const values = form.values
    const company = companyInput(values)
    const docNumber = normalizeDocNumber(values.docNumber)
    create.mutate(
      {
        docNumber,
        docType: docTypeOf(docNumber),
        legalName: company.legalName,
        tradeName: company.tradeName ?? undefined,
        address: { ...company.address, complement: company.address.complement ?? undefined },
        phone: company.phone ?? undefined,
        legalEmail: company.legalEmail,
        timezone: company.timezone,
        audiences: values.audiences,
        // Sem rede, a chave não vai: a clínica nasce sem rede (RN-ADM-50).
        ...(values.networkId ? { networkId: values.networkId } : {}),
        firstManager: {
          name: values.managerName.trim(),
          email: values.managerEmail.trim().toLowerCase(),
          accessMode: values.accessMode,
        },
      },
      {
        onSuccess: (result) => {
          const name = clinicName(result.clinic)
          const { access, user } = result.firstManager
          if (access.mode === 'temporary_password') {
            toast(`Clínica ${name} criada`)
            setCreated(result)
            return
          }
          if (access.mode === 'existing_membership') {
            toast(`Clínica ${name} criada — ${user.email} já tinha conta e ganhou o acesso`)
          } else if (access.emailSent) {
            toast(`Clínica ${name} criada — convite enviado a ${user.email}`)
          } else {
            toast(
              `Clínica ${name} criada — o convite a ${user.email} não saiu; reenvie em Usuários`,
            )
          }
          navigate(routes.admin.clinic(result.clinic.id))
        },
        onError: (error) => {
          form.refuse(placeFieldErrors(error, (path) => fieldKey(path, values)).fields)
          focusFirstInvalid(formRef.current)
        },
      },
    )
  }

  if (created) return <TemporaryPasswordResult created={created} />

  const failure = create.isError
    ? placeFieldErrors(create.error, (path) => fieldKey(path, form.values))
    : null
  const showFailure =
    failure !== null && (Object.keys(failure.fields).length === 0 || failure.unplaced.length > 0)
  const existing =
    create.error && isApiError(create.error) && create.error.code === 'conflict'
      ? (create.error.details.existing as { id: string } | undefined)
      : undefined

  // A consulta é um atalho: a falha vira dica no campo e o cadastro segue à mão.
  let docNumberHint: ReactNode =
    'Com o CNPJ completo, buscamos a razão social e o endereço na base pública.'
  if (lookup.isPending) docNumberHint = 'Buscando os dados do CNPJ…'
  else if (lookup.isError) docNumberHint = errorMessage(lookup.error)
  else if (lookup.data) {
    const status = lookup.data.registrationStatus
    docNumberHint =
      status && status !== 'active' ? (
        <span className="text-plum">
          Na base pública, a situação deste CNPJ é {REGISTRATION_STATUS_LABELS[status]}. Confira
          antes de criar a clínica.
        </span>
      ) : (
        'Dados preenchidos pela base pública. Confira antes de criar: tudo segue editável.'
      )
  }

  return (
    <form ref={formRef} onSubmit={submit} noValidate>
      <PageHeader
        LinkComponent={Link}
        crumbs={[{ label: 'Clínicas', href: routes.admin.clinics }, { label: 'nova' }]}
      />
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title="Nova clínica"
            subtitle="A clínica nasce com a primeira gestora. O resto ela mesma completa na implantação: testemunhas e procedimentos."
          />

          <div className="mt-6 flex flex-col gap-4">
            {showFailure ? (
              <ActionError
                title="Não foi possível criar a clínica"
                description={
                  failure.unplaced.length > 0
                    ? failure.unplaced.join(' ')
                    : `${errorMessage(create.error)} Nada foi criado; o que você digitou continua na tela.`
                }
                actions={
                  existing ? (
                    <Button variant="outline" size="sm" asChild>
                      <Link to={routes.admin.clinic(existing.id)}>Abrir a clínica existente</Link>
                    </Button>
                  ) : undefined
                }
              />
            ) : null}

            <IdentificationBlock
              form={form}
              note="Aparece no cabeçalho e na qualificação das partes de todo documento."
              document={
                <Field label="CNPJ ou CPF" error={form.errors.docNumber} hint={docNumberHint}>
                  <Input
                    autoComplete="off"
                    autoCapitalize="characters"
                    placeholder="00.000.000/0000-00"
                    value={form.values.docNumber}
                    onChange={(event) => changeDocNumber(event.target.value)}
                    onBlur={() => form.touch('docNumber')}
                  />
                </Field>
              }
            />
            <AddressBlock form={form} />
            <ContactBlock form={form} />

            <FormBlock
              title="Primeira gestora"
              note="Recebe o convite por e-mail e faz a implantação da clínica."
            >
              <FieldGrid>
                <TextField form={form} name="managerName" label="Nome" maxLength={120} />
                <TextField
                  form={form}
                  name="managerEmail"
                  label="E-mail"
                  type="email"
                  inputMode="email"
                  autoCapitalize="none"
                  placeholder="nome@clinica.com.br"
                />
              </FieldGrid>
              <div className="eyebrow mt-4" id="first-manager-access-label">
                Acesso
              </div>
              <RadioGroup
                aria-labelledby="first-manager-access-label"
                className="tablet:grid-cols-2 mt-[6px]"
                value={form.values.accessMode}
                onValueChange={(value) => form.update({ accessMode: value as AccessMode })}
              >
                <ChoiceRow
                  control={<Radio value="invite" />}
                  description="Ela recebe um link para definir a senha, válido por 7 dias."
                >
                  Convite por e-mail
                </ChoiceRow>
                <ChoiceRow
                  control={<Radio value="temporary_password" />}
                  description="A senha aparece uma única vez nesta tela, para você entregar. A troca é obrigatória no primeiro acesso."
                >
                  Senha temporária
                </ChoiceRow>
              </RadioGroup>
            </FormBlock>

            <AudiencesBlock
              value={form.values.audiences}
              onChange={(audiences) => form.update({ audiences })}
              error={form.errors.audiences}
            />

            <NetworkBlock
              value={form.values.networkId}
              onChange={(networkId) => form.update({ networkId })}
              error={form.errors.networkId}
            />
            <FormFooter
              secondary={
                <Button variant="outline" asChild>
                  <Link to={routes.admin.clinics}>Cancelar</Link>
                </Button>
              }
              primary={
                <Button type="submit" loading={create.isPending} loadingLabel="Criando a clínica…">
                  Criar clínica
                </Button>
              }
            />
          </div>
        </ReadingColumn>
      </Page>
    </form>
  )
}

/** A clínica foi criada com senha temporária: a senha aparece aqui, uma única vez, com "Copiar". */
function TemporaryPasswordResult({ created }: { created: CreatedClinic }) {
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle')
  const { clinic, firstManager } = created
  const { access, user } = firstManager
  const password = access.mode === 'temporary_password' ? access.temporaryPassword : null
  const name = clinicName(clinic)

  async function copyPassword() {
    if (!password) return
    try {
      await navigator.clipboard.writeText(password)
      setCopy('copied')
      toast('Senha copiada')
    } catch {
      setCopy('failed')
    }
  }

  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[{ label: 'Clínicas', href: routes.admin.clinics }, { label: name }]}
      />
      <Page>
        <ReadingColumn>
          <PageTitle
            title={`Clínica ${name} criada`}
            subtitle="Ela já existe e está em implantação. Falta entregar a senha à primeira gestora."
          />
          <AttentionBlock
            className="mt-6"
            title={`Senha temporária de ${titleCase(user.name)}`}
            aria-label="Senha temporária"
          >
            {password ? (
              <>
                <p>
                  Esta senha não aparece de novo. Copie e entregue a {user.email}; a troca é
                  obrigatória no primeiro acesso.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <code className="border-hairline-strong bg-pampas font-display text-name text-ink border px-[14px] py-[10px] leading-none font-normal select-all">
                    {password}
                  </code>
                  <Button variant="outline" onClick={() => void copyPassword()}>
                    {copy === 'copied' ? 'Copiada' : 'Copiar'}
                  </Button>
                </div>
                {copy === 'failed' ? (
                  <p className="text-plum mt-2 text-[12px] leading-[1.6]">
                    O navegador não deixou copiar. Selecione a senha acima e copie à mão.
                  </p>
                ) : null}
              </>
            ) : (
              <p>
                A clínica já tinha sido criada na primeira tentativa, e a senha só é mostrada uma
                vez. Para {user.email} entrar, abra a clínica, entre nela e reenvie o convite em
                Usuários.
              </p>
            )}
            {!access.emailSent ? (
              <p className="mt-2 text-[12px] leading-[1.6]">
                O e-mail avisando {user.email} do acesso não saiu. Entregue a senha por outro canal.
              </p>
            ) : null}
          </AttentionBlock>
          <div className="mt-6">
            <Button className="max-desk:min-h-touch" asChild>
              <Link to={routes.admin.clinic(clinic.id)}>Abrir a clínica</Link>
            </Button>
          </div>
        </ReadingColumn>
      </Page>
    </>
  )
}
